import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Radio,
  ListItemIcon,
  Paper,
} from '@material-ui/core';
import { Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import Close from '../assets/images/menu-icons/Close.svg?react';
import PdfIcon from '../assets/images/menu-icons/icon-pdf.svg?react';
import GoogleDocIcon from '../assets/images/menu-icons/google-docs.svg?react';
import {
  PDF_FORMAT,
  GOOGLE_DOC_FORMAT,
  PROJECT_DATES_FORMAT_OPTIONS,
  FILE_FORMAT_OPTIONS,
  getAvailableFileFormats,
  isFileFormatEnabled,
  getTemplateUrlByName,
  handleExportPdf,
} from './ExportDialog.helpers';
import { MONTHS_YEARS_FORMAT } from '../constants/exportDateFormatTypes';
import AppContext from '../context/AppContext';

const ExportDialog = ({ templates, choseTemplate, open, onClose, sendData, spinner }) => {
  const context = useContext(AppContext);
  const { dispatch } = context;

  const [chosenTemplate, setChosenTemplate] = useState(choseTemplate);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectedFileFormat, setSelectedFileFormat] = useState(GOOGLE_DOC_FORMAT);
  const [selectedDateFormat, setSelectedDateFormat] = useState(MONTHS_YEARS_FORMAT);
  const [url, setUrl] = useState('');

  const handleClose = () => {
    setSelectedDateFormat(MONTHS_YEARS_FORMAT);
    dispatch({
      type: 'setDateFormat',
      payload: MONTHS_YEARS_FORMAT,
    });
    onClose();
  };

  useEffect(() => {
    const URL = getTemplateUrlByName(templates, choseTemplate);
    setChosenTemplate(choseTemplate);
    setUrl(URL);
  }, [choseTemplate, templates]);

  const availableFormats = useMemo(() => getAvailableFileFormats(chosenTemplate), [chosenTemplate]);

  useEffect(() => {
    if (!availableFormats.includes(selectedFileFormat)) {
      setSelectedFileFormat(availableFormats[0]);
    }
  }, [availableFormats, selectedFileFormat]);

  const getPopupContainer = (triggerNode) => triggerNode.parentNode;

  const handleExport = () => {
    if (selectedFileFormat === GOOGLE_DOC_FORMAT) {
      sendData(url, chosenTemplate, selectedDateFormat);
      onClose();
    }
    if (selectedFileFormat === PDF_FORMAT) {
      handleExportPdf(chosenTemplate, context, spinner, handleClose);
    }
  };

  const renderFileFormatIcon = (fileFormat) =>
    fileFormat === PDF_FORMAT ? <PdfIcon /> : <GoogleDocIcon />;

  const handleChangeDateFormat = (dates) => {
    setSelectedDateFormat(dates);
    dispatch({
      type: 'setDateFormat',
      payload: dates,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted
      PaperProps={{ style: { width: '480px' } }}
    >
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <DialogTitle style={{ padding: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h3 style={{ fontWeight: '600', margin: 0, fontSize: '26px' }}>Export</h3>
            <IconButton size="small" onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontWeight: '600', fontSize: '10.5px', color: '#718096' }}>
            COMPANY TEMPLATE
          </p>
          <Select
            size="large"
            value={chosenTemplate ?? undefined}
            getPopupContainer={getPopupContainer}
            onChange={(value) => setChosenTemplate(value)}
            onDropdownVisibleChange={(openDropdown) => setIsSelectOpen(openDropdown)}
            suffixIcon={
              <DownOutlined
                style={{
                  fontSize: 14,
                  transition: 'transform 0.2s ease',
                  rotate: isSelectOpen ? '180deg' : '0deg',
                  color: '#000',
                }}
              />
            }
          >
            {templates.map(({ _id: templateId, name }) => (
              <Select.Option key={templateId} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <p style={{ fontWeight: '600', fontSize: '10.5px', color: '#718096' }}>FILE FORMAT</p>
          <List component="div" style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {FILE_FORMAT_OPTIONS.map((fileFormat, ind) => (
              <Paper variant="outlined" key={ind}>
                <ListItem
                  style={{ height: '40px', gap: 2, cursor: 'pointer' }}
                  disabled={!isFileFormatEnabled(chosenTemplate, fileFormat)}
                  onClick={() => setSelectedFileFormat(fileFormat)}
                >
                  <ListItemIcon style={{ minWidth: 0 }}>
                    {renderFileFormatIcon(fileFormat)}
                  </ListItemIcon>
                  <ListItemText primary={fileFormat} />
                  <ListItemIcon style={{ minWidth: 0 }}>
                    <Radio
                      value={fileFormat}
                      color="primary"
                      disabled={!isFileFormatEnabled(chosenTemplate, fileFormat)}
                      checked={selectedFileFormat === fileFormat}
                    />
                  </ListItemIcon>
                </ListItem>
              </Paper>
            ))}
          </List>
        </div>

        <div>
          <p style={{ fontWeight: '600', fontSize: '10.5px', color: '#718096' }}>
            PROJECT DATES FORMAT
          </p>
          <List component="div" style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {PROJECT_DATES_FORMAT_OPTIONS.map((dates, ind) => (
              <Paper variant="outlined" key={ind}>
                <ListItem
                  style={{ height: '40px', cursor: 'pointer' }}
                  onClick={() => handleChangeDateFormat(dates)}
                >
                  <ListItemText primary={dates} />
                  <ListItemIcon style={{ minWidth: 0 }}>
                    <Radio
                      value={selectedDateFormat}
                      checked={selectedDateFormat === dates}
                      color="primary"
                    />
                  </ListItemIcon>
                </ListItem>
              </Paper>
            ))}
          </List>
        </div>

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleExport}>
            Export
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default ExportDialog;
